import { api } from './client'
import type { ApiEnvelope, PaginatedResponse, PermissionGroupItem, PermissionGroupDetail, PermissionCategory } from '@/types/api'

export interface PermissionQueryParams {
    keySearch?: string
    status?: number | string
    fromDate?: string
    toDate?: string
}
interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

function adaptPage<T>(raw: SpringPage<T> | PaginatedResponse<T>): PaginatedResponse<T> {
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements,
        page: raw.number + 1,
        limit: raw.size,
        totalPages: raw.totalPages,
    }
}

export async function fetchPermissions(
    page: number = 0,
    pageSize: number = 10,
    params: PermissionQueryParams = {},
    signal?: AbortSignal
): Promise<PaginatedResponse<PermissionGroupItem>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<PermissionGroupItem>>>('/v1/permission-management/group', {
            params: { page, pageSize, ...params },
            signal
        })
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách nhóm quyền.';
        throw new Error(message);
    }
}

export async function getPermissionById(id: string, signal?: AbortSignal): Promise<PermissionGroupDetail> {
    try {
        const response = await api.get<ApiEnvelope<PermissionGroupDetail>>(`/v1/permission-management/group/${id}`, { signal })
        return response.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy thông tin nhóm quyền.';
        throw new Error(message);
    }
}

export async function getPermissionCategories(signal?: AbortSignal): Promise<PermissionCategory[]> {
    try {
        const response = await api.get<ApiEnvelope<PermissionCategory[]>>(`/v1/permission-management/category`, { signal })
        return response.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh mục quyền.';
        throw new Error(message);
    }
}

export async function createPermission(data: {
    name: string
    unitId: string[]
    description: string
    permissionIds: string[]
}): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.post<ApiEnvelope<any>>('/v1/permission-management/group', data)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tạo nhóm quyền.';
        throw new Error(message);
    }
}

export async function updatePermission(
    id: string,
    data: Partial<{
        name: string
        newUnitIds: string[]
        oldUnitIds: string[]
        description: string
        newPermissionIds: string[]
        oldPermissionIds: string[]
        status: number
    }>
): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.put<ApiEnvelope<any>>(`/v1/permission-management/group/${id}`, data)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi cập nhật nhóm quyền.';
        throw new Error(message);
    }
}

export async function deletePermission(id: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.delete<ApiEnvelope<any>>(`/v1/permission-management/group/${id}`)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa nhóm quyền.';
        throw new Error(message);
    }
}